import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, Plus, Edit, Trash2, Eye, Copy, Save, RefreshCw, Search, Filter,
  Mail, MessageSquare, Phone, Smartphone, Globe, Settings, TestTube,
  CheckCircle2, AlertCircle, Clock, User, Calendar, FileText, Tag,
  ChevronDown, ChevronRight, MoreHorizontal, Download, Upload, Share2, X,
  Wifi, WifiOff, Signal, SignalHigh, SignalLow, Activity, TrendingUp, TrendingDown,
  Zap, Target, Shield, Key, Lock, Unlock, Database, Server, HardDrive, Cpu, Network
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const NotificationChannels = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [activeTab, setActiveTab] = useState('channels'); // 'channels', 'testing', 'analytics'

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    setLoading(true);
    try {
      const response = await new AdminService().getNotificationChannels();
      if (response.success) {
        setChannels(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to load notification channels.');
      console.error('Error loading channels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChannel = async (channelData) => {
    try {
      const response = await new AdminService().createNotificationChannel(channelData);
      if (response.success) {
        await loadChannels();
        setShowCreateForm(false);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to create channel.');
      console.error('Error creating channel:', err);
    }
  };

  const handleUpdateChannel = async (channelId, channelData) => {
    try {
      const response = await new AdminService().updateNotificationChannel(channelId, channelData);
      if (response.success) {
        await loadChannels();
        setEditingChannel(null);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to update channel.');
      console.error('Error updating channel:', err);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (window.confirm('Are you sure you want to delete this channel?')) {
      try {
        const response = await new AdminService().deleteNotificationChannel(channelId);
        if (response.success) {
          await loadChannels();
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError('Failed to delete channel.');
        console.error('Error deleting channel:', err);
      }
    }
  };

  const handleTestChannel = async (channelId, testData) => {
    try {
      const response = await new AdminService().testNotificationChannel(channelId, testData);
      if (response.success) {
        alert('Channel test completed successfully!');
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to test channel.');
      console.error('Error testing channel:', err);
    }
  };

  const filteredChannels = channels.filter(channel => {
    const matchesSearch = channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         channel.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || channel.status === filterStatus;
    const matchesType = filterType === 'all' || channel.type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getChannelIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="w-5 h-5" />;
      case 'sms': return <Smartphone className="w-5 h-5" />;
      case 'push': return <Bell className="w-5 h-5" />;
      case 'in_app': return <MessageSquare className="w-5 h-5" />;
      case 'webhook': return <Globe className="w-5 h-5" />;
      case 'slack': return <MessageSquare className="w-5 h-5" />;
      case 'teams': return <MessageSquare className="w-5 h-5" />;
      case 'discord': return <MessageSquare className="w-5 h-5" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'testing': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle2 className="w-4 h-4" />;
      case 'inactive': return <Clock className="w-4 h-4" />;
      case 'error': return <AlertCircle className="w-4 h-4" />;
      case 'testing': return <TestTube className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600 dark:text-gray-300">Loading notification channels...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Bell className="mr-3" size={28} />
            Notification Channels
          </h1>
          <p className="text-gray-700 dark:text-gray-300 mt-2">
            Configure and manage notification delivery channels
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
           style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Channel
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'channels', label: 'Channels', icon: Bell },
            { id: 'testing', label: 'Testing', icon: TestTube },
            { id: 'analytics', label: 'Analytics', icon: Activity }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Channels Tab */}
      {activeTab === 'channels' && (
        <>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search channels..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="error">Error</option>
              <option value="testing">Testing</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="push">Push</option>
              <option value="in_app">In-App</option>
              <option value="webhook">Webhook</option>
              <option value="slack">Slack</option>
              <option value="teams">Teams</option>
              <option value="discord">Discord</option>
            </select>
          </div>

          {/* Channels Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredChannels.map(channel => (
              <div key={channel.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      {getChannelIcon(channel.type)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                        {channel.name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">
                        {channel.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingChannel(channel)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleTestChannel(channel.id, {})}
                      className="p-1 text-gray-400 hover:text-green-600"
                      title="Test"
                    >
                      <TestTube className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteChannel(channel.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-2 mb-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(channel.status)}`}>
                    {getStatusIcon(channel.status)}
                    {channel.status}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-full text-xs font-medium">
                    {channel.type}
                  </span>
                </div>

                {/* Configuration */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Configuration:</h4>
                  <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                    {channel.config && Object.entries(channel.config).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="capitalize">{key}:</span>
                        <span className="font-mono text-xs">{typeof value === 'string' && value.length > 20 ? `${value.substring(0, 20)}...` : value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Performance:</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-center p-2 bg-gray-50 dark:bg-gray-600 rounded">
                      <div className="font-semibold text-green-600">{channel.metrics?.successRate || '95%'}</div>
                      <div className="text-xs text-gray-500">Success Rate</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 dark:bg-gray-600 rounded">
                      <div className="font-semibold text-blue-600">{channel.metrics?.avgDeliveryTime || '2.3s'}</div>
                      <div className="text-xs text-gray-500">Avg Delivery</div>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>{channel.metrics?.totalSent || 0} sent</span>
                  <span>Updated {new Date(channel.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>

          {filteredChannels.length === 0 && (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No channels found</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Add your first notification channel to get started'
                }
              </p>
              {!searchTerm && filterStatus === 'all' && filterType === 'all' && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center mx-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Channel
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Testing Tab */}
      {activeTab === 'testing' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <TestTube className="w-5 h-5 mr-2" />
              Channel Testing
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Test Configuration</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Channel
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option>Select a channel to test</option>
                      {channels.map(channel => (
                        <option key={channel.id} value={channel.id}>
                          {channel.name} ({channel.type})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Test Message
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Enter test message..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Recipient
                    </label>
                    <input
                      type="text"
                      placeholder="test@example.com or +1234567890"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center">
                    <TestTube className="w-4 h-4 mr-2" />
                    Send Test
                  </button>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Test Results</h4>
                <div className="space-y-3">
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Email Channel</span>
                      <span className="text-green-600 text-sm">✓ Success</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Delivered to test@example.com in 1.2s
                    </div>
                  </div>
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">SMS Channel</span>
                      <span className="text-green-600 text-sm">✓ Success</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Delivered to +1234567890 in 0.8s
                    </div>
                  </div>
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Push Channel</span>
                      <span className="text-yellow-600 text-sm">⚠ Warning</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Delivered but user may not have push enabled
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Messages</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">12,456</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Success Rate</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">94.2%</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Avg Delivery</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">2.1s</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Failed</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">723</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Activity className="w-5 h-5 mr-2" />
              Channel Performance
            </h3>
            <div className="space-y-4">
              {channels.slice(0, 5).map(channel => (
                <div key={channel.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      {getChannelIcon(channel.type)}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">{channel.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{channel.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {channel.metrics?.successRate || '95%'}
                      </div>
                      <div className="text-xs text-gray-500">Success Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {channel.metrics?.avgDeliveryTime || '2.3s'}
                      </div>
                      <div className="text-xs text-gray-500">Avg Delivery</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {channel.metrics?.totalSent || 0}
                      </div>
                      <div className="text-xs text-gray-500">Total Sent</div>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(channel.status)}`}>
                      {getStatusIcon(channel.status)}
                      {channel.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Channel Modal */}
      {(showCreateForm || editingChannel) && (
        <ChannelForm
          channel={editingChannel}
          onSave={editingChannel ? handleUpdateChannel : handleCreateChannel}
          onCancel={() => {
            setShowCreateForm(false);
            setEditingChannel(null);
          }}
        />
      )}
    </div>
  );
};

// Channel Form Component
const ChannelForm = ({ channel, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: channel?.name || '',
    description: channel?.description || '',
    type: channel?.type || 'email',
    status: channel?.status || 'inactive',
    config: channel?.config || {},
    priority: channel?.priority || 1,
    retryAttempts: channel?.retryAttempts || 3,
    timeout: channel?.timeout || 30
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(channel?.id, formData);
  };

  const handleConfigChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [key]: value
      }
    }));
  };

  const getConfigFields = (type) => {
    switch (type) {
      case 'email':
        return [
          { key: 'smtp_host', label: 'SMTP Host', type: 'text', placeholder: 'smtp.gmail.com' },
          { key: 'smtp_port', label: 'SMTP Port', type: 'number', placeholder: '587' },
          { key: 'username', label: 'Username', type: 'email', placeholder: 'user@example.com' },
          { key: 'password', label: 'Password', type: 'password', placeholder: '••••••••' },
          { key: 'from_email', label: 'From Email', type: 'email', placeholder: 'noreply@example.com' }
        ];
      case 'sms':
        return [
          { key: 'provider', label: 'Provider', type: 'select', options: ['Twilio', 'AWS SNS', 'Vonage'] },
          { key: 'api_key', label: 'API Key', type: 'text', placeholder: 'Your API key' },
          { key: 'api_secret', label: 'API Secret', type: 'password', placeholder: '••••••••' },
          { key: 'from_number', label: 'From Number', type: 'text', placeholder: '+1234567890' }
        ];
      case 'push':
        return [
          { key: 'firebase_key', label: 'Firebase Key', type: 'text', placeholder: 'Your Firebase key' },
          { key: 'project_id', label: 'Project ID', type: 'text', placeholder: 'your-project-id' }
        ];
      case 'webhook':
        return [
          { key: 'url', label: 'Webhook URL', type: 'url', placeholder: 'https://example.com/webhook' },
          { key: 'secret', label: 'Secret', type: 'password', placeholder: '••••••••' },
          { key: 'method', label: 'Method', type: 'select', options: ['POST', 'PUT', 'PATCH'] }
        ];
      case 'slack':
        return [
          { key: 'webhook_url', label: 'Webhook URL', type: 'url', placeholder: 'https://hooks.slack.com/...' },
          { key: 'channel', label: 'Channel', type: 'text', placeholder: '#general' }
        ];
      default:
        return [];
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {channel ? 'Edit Channel' : 'Add Channel'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Channel Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Channel Type *
              </label>
              <select
                required
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value, config: {} }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="push">Push Notification</option>
                <option value="in_app">In-App</option>
                <option value="webhook">Webhook</option>
                <option value="slack">Slack</option>
                <option value="teams">Microsoft Teams</option>
                <option value="discord">Discord</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Configuration Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Configuration
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getConfigFields(formData.type).map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {field.label}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      value={formData.config[field.key] || ''}
                      onChange={(e) => handleConfigChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select {field.label}</option>
                      {field.options.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      placeholder={field.placeholder}
                      value={formData.config[field.key] || ''}
                      onChange={(e) => handleConfigChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="inactive">Inactive</option>
                <option value="active">Active</option>
                <option value="testing">Testing</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Priority
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Retry Attempts
              </label>
              <input
                type="number"
                min="0"
                max="10"
                value={formData.retryAttempts}
                onChange={(e) => setFormData(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <Save className="w-4 h-4 mr-2" />
              {channel ? 'Update Channel' : 'Add Channel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotificationChannels;
